import { useState } from 'react'
import { motion, Reorder } from 'framer-motion'
import { Plus, GripVertical, Edit2, Trash2, Eye, EyeOff, Image, Video } from 'lucide-react'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { heroSlides as defaultSlides } from '../../data/seedData'
import { Button, Modal, Input, Textarea, Card, Badge, Checkbox } from '../../components/common'

const HeroSlideForm = ({ slide, onSave, onClose }) => {
  const [formData, setFormData] = useState(slide || {
    title: '',
    subtitle: '',
    imageUrl: '',
    videoUrl: '',
    ctaText: '',
    ctaLink: '',
    order: 0,
    isActive: true,
  })
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useFileUpload()

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const result = await upload(file, `hero/${Date.now()}_${file.name}`)
    if (result.success) {
      setFormData({ ...formData, imageUrl: result.url })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Title"
        required
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="Welcome to Deeper Life"
      />

      <Textarea
        label="Subtitle"
        rows={2}
        value={formData.subtitle}
        onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
        placeholder="Experience God's transforming power"
      />

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Background Image
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-4">
            {formData.imageUrl ? (
              <div className="relative">
                <img
                  src={formData.imageUrl}
                  alt="Preview"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, imageUrl: '' })}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center cursor-pointer">
                <Image className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Click to upload</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
          <Input
            className="mt-2"
            placeholder="Or enter image URL"
            value={formData.imageUrl}
            onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
          />
        </div>

        <div>
          <Input
            label="Video URL (optional)"
            value={formData.videoUrl}
            onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
            placeholder="https://youtube.com/watch?v=..."
            helperText="Video will be used instead of image if provided"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Input
          label="CTA Button Text"
          value={formData.ctaText}
          onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
          placeholder="Learn More"
        />
        <Input
          label="CTA Button Link"
          value={formData.ctaLink}
          onChange={(e) => setFormData({ ...formData, ctaLink: e.target.value })}
          placeholder="/about"
        />
      </div>

      <div className="flex items-center gap-4">
        <Checkbox
          label="Active (visible on website)"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving || uploading}>
          {slide ? 'Update Slide' : 'Create Slide'}
        </Button>
      </div>
    </form>
  )
}

const HeroManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editingSlide, setEditingSlide] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data: slides, add, update, remove, loading } = useCollection('heroSlides', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const displaySlides = slides?.length > 0 ? slides : defaultSlides

  const handleSave = async (data) => {
    if (editingSlide?.id) {
      await update(editingSlide.id, data)
    } else {
      await add({ ...data, order: displaySlides.length })
    }
    setShowModal(false)
    setEditingSlide(null)
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await remove(deleteConfirm)
      setDeleteConfirm(null)
    }
  }

  const handleReorder = async (newOrder) => {
    // Update order for each slide
    for (let i = 0; i < newOrder.length; i++) {
      if (newOrder[i].order !== i) {
        await update(newOrder[i].id, { order: i })
      }
    }
  }

  const toggleActive = async (slide) => {
    await update(slide.id, { isActive: !slide.isActive })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Hero Slides</h1>
          <p className="text-gray-500">Manage the homepage hero carousel</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingSlide(null)
            setShowModal(true)
          }}
        >
          Add Slide
        </Button>
      </div>

      {/* Slides List */}
      <Card animate={false} className="p-6">
        <p className="text-sm text-gray-500 mb-4">
          Drag and drop to reorder slides. The first active slide will be shown first.
        </p>

        <Reorder.Group
          axis="y"
          values={displaySlides}
          onReorder={handleReorder}
          className="space-y-3"
        >
          {displaySlides.map((slide) => (
            <Reorder.Item
              key={slide.id}
              value={slide}
              className="bg-gray-50 rounded-xl p-4 cursor-move"
            >
              <div className="flex items-center gap-4">
                <GripVertical className="w-5 h-5 text-gray-400" />

                <div className="w-32 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                  {slide.imageUrl ? (
                    <img
                      src={slide.imageUrl}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                    />
                  ) : slide.videoUrl ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                      <Video className="w-8 h-8 text-gray-500" />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-dark-100 truncate">{slide.title}</h3>
                  <p className="text-sm text-gray-500 truncate">{slide.subtitle}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      variant={slide.isActive ? 'success' : 'gray'}
                      size="sm"
                    >
                      {slide.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    {slide.ctaText && (
                      <Badge variant="primary" size="sm">
                        CTA: {slide.ctaText}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(slide)}
                    className={`p-2 rounded-lg transition-colors ${
                      slide.isActive
                        ? 'text-emerald-600 hover:bg-emerald-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={slide.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {slide.isActive ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditingSlide(slide)
                      setShowModal(true)
                    }}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(slide.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>

        {displaySlides.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No hero slides yet</p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => setShowModal(true)}
            >
              Create Your First Slide
            </Button>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingSlide(null)
        }}
        title={editingSlide ? 'Edit Hero Slide' : 'Create Hero Slide'}
        size="lg"
      >
        <HeroSlideForm
          slide={editingSlide}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingSlide(null)
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Slide"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this slide? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default HeroManager
